// Team members service to share members across components

const TEAM_MEMBERS_KEY = 'app_team_members';
const ACTION_ITEMS_KEY = 'app_action_items';

// Interfaz para los Action Items
export interface ActionItem {
  id: string;
  title: string;
  description: string;
  ownerId: string; // ID del miembro del equipo asignado (su nombre)
  status: 'pending' | 'in-progress' | 'completed';
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Funciones para miembros del equipo
export const getTeamMembers = (): string[] => {
  const storedMembers = localStorage.getItem(TEAM_MEMBERS_KEY);
  if (storedMembers) {
    try {
      return JSON.parse(storedMembers);
    } catch (e) {
      console.error('Error parsing team members from localStorage', e);
      return [];
    }
  }
  return [];
};

export const saveTeamMembers = (members: string[]): void => {
  try {
    // Remove any duplicates and empty strings
    const uniqueMembers = [...new Set(members)].filter(m => m.trim() !== '');
    localStorage.setItem(TEAM_MEMBERS_KEY, JSON.stringify(uniqueMembers));
    
    // Dispatch a custom event so other components can react
    window.dispatchEvent(new CustomEvent('team-members-updated', { 
      detail: { members: uniqueMembers } 
    }));
  } catch (e) {
    console.error('Error saving team members to localStorage', e);
  }
};

export const addTeamMember = (member: string): string[] => {
  if (!member.trim()) return getTeamMembers();
  
  const currentMembers = getTeamMembers();
  if (currentMembers.includes(member.trim())) {
    return currentMembers; // Member already exists
  }
  
  const updatedMembers = [...currentMembers, member.trim()];
  saveTeamMembers(updatedMembers);
  return updatedMembers;
};

export const removeTeamMember = (member: string): string[] => {
  const currentMembers = getTeamMembers();
  const updatedMembers = currentMembers.filter(m => m !== member);
  saveTeamMembers(updatedMembers);
  
  // Actualizar los action items que tenían a este miembro como owner
  const actionItems = getActionItems();
  const updatedActionItems = actionItems.map(item => {
    if (item.ownerId === member) {
      return { ...item, ownerId: '' }; // Eliminar el owner si el miembro se elimina
    }
    return item;
  });
  saveActionItems(updatedActionItems);
  
  return updatedMembers;
};

// Funciones para action items
export const getActionItems = (): ActionItem[] => {
  const storedItems = localStorage.getItem(ACTION_ITEMS_KEY);
  if (storedItems) {
    try {
      return JSON.parse(storedItems);
    } catch (e) {
      console.error('Error parsing action items from localStorage', e);
      return [];
    }
  }
  return [];
};

export const saveActionItems = (items: ActionItem[]): void => {
  try {
    localStorage.setItem(ACTION_ITEMS_KEY, JSON.stringify(items));
    
    // Dispatch a custom event so other components can react
    window.dispatchEvent(new CustomEvent('action-items-updated', { 
      detail: { items } 
    }));
  } catch (e) {
    console.error('Error saving action items to localStorage', e);
  }
};

export const addActionItem = (
  title: string, 
  description: string = '', 
  ownerId: string = '',
  dueDate?: string
): ActionItem[] => {
  if (!title.trim()) return getActionItems();
  
  const newItem: ActionItem = {
    id: generateId(),
    title: title.trim(),
    description: description.trim(),
    ownerId: ownerId,
    status: 'pending',
    dueDate: dueDate,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  const currentItems = getActionItems();
  const updatedItems = [...currentItems, newItem];
  saveActionItems(updatedItems);
  return updatedItems;
};

export const updateActionItem = (
  id: string, 
  updates: Partial<Omit<ActionItem, 'id' | 'createdAt'>>
): ActionItem[] => {
  const currentItems = getActionItems();
  const itemIndex = currentItems.findIndex(item => item.id === id);
  
  if (itemIndex === -1) return currentItems; // Item not found
  
  const updatedItem = {
    ...currentItems[itemIndex],
    ...updates,
    updatedAt: new Date().toISOString()
  };
  
  const updatedItems = [
    ...currentItems.slice(0, itemIndex),
    updatedItem,
    ...currentItems.slice(itemIndex + 1)
  ];
  
  saveActionItems(updatedItems);
  return updatedItems;
};

export const assignActionItemOwner = (
  actionItemId: string, 
  ownerId: string
): ActionItem[] => {
  return updateActionItem(actionItemId, { ownerId });
};

export const updateActionItemStatus = (
  actionItemId: string, 
  status: ActionItem['status']
): ActionItem[] => {
  return updateActionItem(actionItemId, { status });
};

export const removeActionItem = (id: string): ActionItem[] => {
  const currentItems = getActionItems();
  const updatedItems = currentItems.filter(item => item.id !== id);
  saveActionItems(updatedItems);
  return updatedItems;
};

export const getActionItemsByOwner = (ownerId: string): ActionItem[] => {
  const allItems = getActionItems();
  return allItems.filter(item => item.ownerId === ownerId);
};

export const getUnassignedActionItems = (): ActionItem[] => {
  const allItems = getActionItems();
  return allItems.filter(item => !item.ownerId);
};

// Función auxiliar para generar IDs únicos
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
}; 